import { useEffect, useState, useCallback, useRef } from "react";
import { Database, Warning, CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DbStatusMonitor — shows a staged popup explaining what the app is doing to reach the DB.
 *
 * Flow:
 *   1. Mount → "Trying to reach database at <primary host>..."
 *   2. Backend probes primary; if that fails, it automatically probes the env default.
 *   3. UI reacts to the response:
 *        - primary OK + fast:     silently dismiss
 *        - primary OK + slow:     flash "✓ Connected" then dismiss
 *        - primary FAILED + fallback OK:  show "retrying default…" then "✓ Connected to default" then dismiss
 *        - both FAILED:           persistent error popup with Retry
 */
export default function DbStatusMonitor() {
  const [phase, setPhase] = useState("probing");
  const [data, setData] = useState(null);
  const [startedAt, setStartedAt] = useState(0);
  const [visible, setVisible] = useState(true);
  const probedRef = useRef(false); // guard against StrictMode double-invocation

  const probe = useCallback(async (opts = {}) => {
    if (probedRef.current && !opts.force) return;
    probedRef.current = true;
    setPhase("probing");
    setData(null);
    setVisible(true);
    setStartedAt(Date.now());
    try {
      // iter193 — the initial mount reads the cheap circuit state
      // (no MariaDB TCP handshake). Explicit retry (from the "Retry
      // connection" button) MUST send ?probe=true so the backend
      // actually attempts the live TCP handshake and can heal a
      // stuck-open circuit.
      const url = opts.probe
        ? `${API}/db-status?probe=true`
        : `${API}/db-status`;
      const res = await fetch(url, { credentials: "include" });
      const json = await res.json();
      setData(json);
    } catch (e) {
      setData({ connected: false, primary: { host_prefix: "—", connected: false, error: String(e) }, fallback: null });
    }
  }, []);

  const retry = useCallback(() => {
    probedRef.current = false;
    probe({ force: true, probe: true });
  }, [probe]);

  useEffect(() => { probe(); }, [probe]);

  // Drive the UX sequence off the backend response.
  useEffect(() => {
    if (!data) return;
    const elapsed = Date.now() - (startedAt || Date.now());

    // Happy path — primary connected directly.
    if (data.connected && !data.fallback_used) {
      if (elapsed < 350) { setVisible(false); return; } // too fast to bother
      setPhase("ok");
      const t = setTimeout(() => setVisible(false), 1500);
      return () => clearTimeout(t);
    }

    // Primary failed, env default worked.
    if (data.fallback_used) {
      setPhase("retrying");
      const t1 = setTimeout(() => setPhase("fallback-ok"), 1800);
      const t2 = setTimeout(() => setVisible(false), 4200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    // Both failed — persistent error.
    setPhase("error");
  }, [data, startedAt]);

  if (!visible) return null;

  const primary = data?.primary || null;
  const fallback = data?.fallback || null;

  return (
    <div className="fixed top-16 right-6 z-[9999] w-[360px] max-w-[calc(100vw-24px)]" data-testid="db-status-popup" data-phase={phase}>
      <div className={`rounded-sm border shadow-xl bg-white overflow-hidden animate-[slideInRight_0.25s_ease-out] ${BORDER[phase]}`}>
        <div className={`px-4 py-3 flex items-start gap-3 ${BG[phase]}`}>
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${ICON_BG[phase]}`}>
            <Icon phase={phase} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{TITLE[phase]}</div>
            <div className="text-sm font-semibold text-slate-900 font-['Chivo'] leading-tight mt-0.5 break-words">
              {phase === "probing" && (
                <>Trying to reach database at <span className="font-mono text-blue-700">{primary?.host_prefix || "…"}</span></>
              )}
              {phase === "retrying" && (
                <>Primary <span className="font-mono">{primary?.host_prefix}</span> unreachable — trying default <span className="font-mono text-blue-700">{fallback?.host_prefix}</span></>
              )}
              {phase === "ok" && (
                <>Connected to <span className="font-mono text-emerald-700">{primary?.host_prefix}</span></>
              )}
              {phase === "fallback-ok" && (
                <>Connected to default <span className="font-mono text-emerald-700">{fallback?.host_prefix}</span></>
              )}
              {phase === "error" && (
                <>Database unreachable</>
              )}
            </div>
            {phase === "error" && (
              <div className="text-[11px] text-slate-600 mt-1.5 break-words">
                Tried <span className="font-mono">{primary?.host_prefix}</span>
                {fallback && <> and <span className="font-mono">{fallback?.host_prefix}</span></>}
                {primary?.error && <div className="text-red-600 mt-1 line-clamp-3" title={primary.error}>{primary.error}</div>}
              </div>
            )}
            {phase === "fallback-ok" && primary?.host_prefix && (
              <div className="text-[11px] text-amber-700 mt-1">
                Admin-override host <span className="font-mono">{primary.host_prefix}</span> was down; using the default for now.
              </div>
            )}
          </div>
          <button onClick={() => setVisible(false)} data-testid="db-status-popup-close"
            className="w-6 h-6 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-600 shrink-0" title="Dismiss">
            ✕
          </button>
        </div>
        {phase === "error" && (
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button onClick={retry} data-testid="db-status-retry"
              className="text-[11px] font-semibold text-blue-700 hover:underline">Retry connection</button>
          </div>
        )}
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(20px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }`}</style>
    </div>
  );
}

const TITLE = {
  probing: "Connecting",
  retrying: "Falling back",
  ok: "Database online",
  "fallback-ok": "Using default database",
  error: "Connection failed",
};
const BG = {
  probing: "bg-blue-50/60",
  retrying: "bg-amber-50/70",
  ok: "bg-emerald-50/60",
  "fallback-ok": "bg-amber-50/70",
  error: "bg-red-50/70",
};
const BORDER = {
  probing: "border-blue-200",
  retrying: "border-amber-300",
  ok: "border-emerald-200",
  "fallback-ok": "border-amber-300",
  error: "border-red-300",
};
const ICON_BG = {
  probing: "bg-blue-100 text-blue-700",
  retrying: "bg-amber-100 text-amber-700",
  ok: "bg-emerald-100 text-emerald-700",
  "fallback-ok": "bg-amber-100 text-amber-800",
  error: "bg-red-100 text-red-700",
};

function Icon({ phase }) {
  if (phase === "probing") return <Spinner size={18} weight="bold" className="animate-spin" />;
  if (phase === "retrying") return <Warning size={18} weight="fill" />;
  if (phase === "ok" || phase === "fallback-ok") return <CheckCircle size={18} weight="fill" />;
  if (phase === "error") return <XCircle size={18} weight="fill" />;
  return <Database size={18} weight="bold" />;
}
